import React, { JSX } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';
import { Box, Paper } from '@mui/material';

// Utils
import { TOTAL_ROW_ID } from '../../../utils/columns/makeStockFacilityColumns';

// Types
import { StockFacilityResult } from '../../../types/Stock';

// Components
import StockFacilityMap from './StockFacilityMap';

// Common Components
import { Table } from '../../common/table/index';

// Space taken by the app header, breadcrumb, page heading and filter bar above
// the panel. Tuned against the live layout; the panel also has a min-height, so
// an imperfect estimate degrades into a slightly shorter panel, never a clipped one.
const PANEL_VIEWPORT_OFFSET = 320;
const PANEL_MIN_HEIGHT = 500;

interface StockResultsPanelProps {
  rows: StockFacilityResult[];
  dataWithTotals: StockFacilityResult[];
  table: any;
  tableLoading: boolean;
  selectedRowIndex: number | null;
  onSelectedRowIndexChange: (index: number | null) => void;
}

export default function StockResultsPanel({
  rows,
  dataWithTotals,
  table,
  tableLoading,
  selectedRowIndex,
  onSelectedRowIndexChange,
}: StockResultsPanelProps): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'stretch',
        // Fills the viewport on larger screens instead of sitting at a fixed
        // 500px; PANEL_MIN_HEIGHT keeps it usable on short ones.
        height: {
          xs: PANEL_MIN_HEIGHT,
          lg: `calc(100vh - ${PANEL_VIEWPORT_OFFSET}px)`,
        },
        minHeight: PANEL_MIN_HEIGHT,
      }}
    >
      <Paper
        sx={{
          flex: '0 0 calc(45% - 8px)',
          minWidth: 0,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <StockFacilityMap facilities={rows} selectedIndex={selectedRowIndex} onSelect={onSelectedRowIndexChange} />
      </Paper>

      {/* Column layout with no overflow of its own: the table's internal scroll
          region is the only scrollbar inside this panel. */}
      <Paper
        sx={{
          flex: '0 0 calc(55% - 8px)',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <h4
          style={{
            margin: 0,
            flex: '0 0 auto',
          }}
        >
          Facility Breakdown
        </h4>
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          <Table
            {...table}
            data={dataWithTotals}
            loading={tableLoading}
            showSearchBar={false}
            showColumnToggler={false}
            showAction={false}
            canExportData={() => false}
            getCoreRowModel={getCoreRowModel()}
            fillHeight
            // Filler rows would render below the sticky totals row; the flex
            // layout already keeps the card from collapsing.
            minRows={0}
            onRowClick={(row: StockFacilityResult) => {
              if (row.facilityId === TOTAL_ROW_ID) {
                return;
              }
              const index = rows.indexOf(row);
              onSelectedRowIndexChange(selectedRowIndex === index ? null : index);
            }}
            rowProps={(row: { index: number; original: StockFacilityResult }) => {
              if (row.original.facilityId === TOTAL_ROW_ID) {
                return {
                  className: 'ui-table__total-row',
                };
              }
              return row.index === selectedRowIndex
                ? {
                    style: {
                      backgroundColor: '#eef2ff',
                    },
                  }
                : {};
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
